import { useEffect, useState } from 'react'
import { IconArrowUpRight, IconPercentage } from '@tabler/icons-react'
import { getSalesAnalytics, type BillingPlan } from '../../../lib/api'
import { NEXT_PLAN, PLAN_LABEL, PLATFORM_FEE_PERCENT } from '../../../lib/planLimits'
import { lowestMonthlyPrice } from '../../../lib/planPricing'
import { useRegion } from '../../../lib/region'
import { useI18n } from '../../../lib/i18n'
import { formatMoney } from './offerShared'

/**
 * La comisión de véltica en el plan gratis, contada en dinero y con a dónde ir.
 *
 * Era una fila de porcentajes —«Tu plan: 5% · Con Pro: 2%»— sin nada que
 * hacer con ella: se leía y no llevaba a ninguna parte. Ahora siempre dice algo
 * con cifras y termina en el plan que lo mejora:
 *
 *   · Sin ventas todavía, con un ejemplo: «Por cada venta de $100 recibes $95;
 *     con Pro, $98». Un porcentaje hay que calcularlo; lo que te llega, no.
 *   · Con ventas en los últimos treinta días, con las suyas: cuánto dejó de
 *     comisión y cuánto habría dejado con Pro. Y si el ahorro ya pasa de lo que
 *     cuesta Pro, lo dice — a partir de ahí el plan se paga solo.
 *
 * Sólo en el gratis. En Pro no hay plan que vender —Business todavía no se
 * contrata—, así que ahí la comisión es un dato más del estado de la cuenta y
 * va en el panel de arriba (ver `StripeSettings`), no en una tarjeta.
 *
 * El ahorro se calcula sobre lo cobrado ANTES de devoluciones (`gross`): la
 * comisión no se devuelve al reembolsar (ver los términos), así que es lo que
 * de verdad pagó. Es aproximado —el servidor la calcula sin impuestos y con el
 * cupón más generoso— y así se dice.
 */
export function PlatformFeeCard({ projectId, token, plan, onOpenPlans }: {
  projectId: string
  token: string
  plan: BillingPlan
  onOpenPlans?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const { currency: projectCurrency } = useRegion()
  /* El plan al que se sube, sólo si es de pago: de él salen el precio y la
     comisión con la que se compara. */
  const nextPlan = NEXT_PLAN[plan]
  const next = nextPlan === 'pro' || nextPlan === 'business' ? nextPlan : null
  const shown = plan === 'free' && next !== null
  const [sales, setSales] = useState<{ gross: number; orders: number; currency: string } | null>(null)

  useEffect(() => {
    if (!shown) return
    let alive = true
    getSalesAnalytics(token, projectId, { range: '30d' })
      .then((stats) => {
        if (alive) setSales({ gross: stats.totals.gross, orders: stats.totals.orders, currency: stats.currency })
      })
      /* Sin datos se cuenta con el ejemplo, que no necesita ninguno. */
      .catch(() => {})
    return () => { alive = false }
  }, [shown, projectId, token])

  if (!shown || !next) return null

  const fee = PLATFORM_FEE_PERCENT[plan]
  const nextFee = PLATFORM_FEE_PERCENT[next]
  const paid = sales ? Math.round((sales.gross * fee) / 100) : 0
  const wouldPay = sales ? Math.round((sales.gross * nextFee) / 100) : 0
  const saving = paid - wouldPay
  const hasSales = sales !== null && sales.orders > 0 && saving > 0
  /* El precio de Pro está en dólares: sólo se compara con ventas en dólares.
     Contra pesos o euros haría falta un tipo de cambio, y un «se paga solo»
     calculado a ojo es una promesa que puede no cumplirse. */
  const paysForItself = hasSales && sales.currency.toLowerCase() === 'usd'
    && saving >= Math.round(lowestMonthlyPrice(next) * 100)

  /* La base: lo que vendió en treinta días o, sin ventas, un ejemplo de cien
     en la moneda en que vende —o la del proyecto si aún no—. En unidades
     mínimas, como todo el comercio. */
  const currency = hasSales ? sales.currency : (sales?.currency || projectCurrency || 'usd')
  const base = hasSales ? sales.gross : 10_000
  const feeNow = hasSales ? paid : Math.round((base * fee) / 100)
  const feeNext = hasSales ? wouldPay : Math.round((base * nextFee) / 100)
  const money = (amount: number) => formatMoney(amount, currency)

  /*
   * Dos bloques lado a lado y no un párrafo: lo que recibes hoy y lo que
   * recibirías con el plan siguiente, con la diferencia en grande. Es la misma
   * comparación que antes se contaba en una frase, pero se lee de un vistazo —
   * como el «antes y después» de cualquier página de precios.
   */
  return (
    <section className="dash-settings-card dash-general-card dash-platform-fee-card">
      <div className="dash-settings-card-head">
        <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
          <IconPercentage size={18} />
        </span>
        <div className="dash-settings-card-head-copy">
          <h3>{hasSales ? t('platformFee.sales.heading', { gross: money(base) }) : t('platformFee.example.heading', { sample: money(base) })}</h3>
          <p>{t('platformFee.stripeNote')}</p>
        </div>
      </div>

      <div className="dash-platform-fee-compare">
        <div className="dash-platform-fee-tile">
          <span className="dash-platform-fee-tile-label">{t('platformFee.tile.current', { plan: PLAN_LABEL[plan] })}</span>
          <strong>{money(base - feeNow)}</strong>
          <span className="dash-platform-fee-tile-sub">{t('platformFee.tile.fee', { amount: money(feeNow), fee })}</span>
        </div>
        <div className="dash-platform-fee-tile is-next">
          <span className="dash-platform-fee-tile-label">{t('platformFee.tile.next', { plan: PLAN_LABEL[next] })}</span>
          <strong>{money(base - feeNext)}</strong>
          <span className="dash-platform-fee-tile-sub is-gain">{t('platformFee.tile.more', { amount: money(feeNow - feeNext) })}</span>
          {onOpenPlans ? (
            <button type="button" className="dash-platform-fee-cta" onClick={onOpenPlans}>
              {t('platformFee.seePlans', { plan: PLAN_LABEL[next] })}
              <IconArrowUpRight size={14} />
            </button>
          ) : null}
        </div>
        {/* El techo: sin comisión de véltica. Business todavía no se vende, así
            que va apagado, con «Próximamente» y sin botón —el botón es de lo que
            sí se puede contratar hoy—. Enseñarlo sirve igual: dice hasta dónde
            llega la escalera y hace de Pro el primer peldaño, no el último. */}
        {next !== 'business' ? (
          <div className="dash-platform-fee-tile is-soon">
            <span className="dash-platform-fee-tile-label">
              {t('platformFee.tile.next', { plan: PLAN_LABEL.business })}
              <em>{t('plans.soon')}</em>
            </span>
            <strong>{money(base - Math.round((base * PLATFORM_FEE_PERCENT.business) / 100))}</strong>
            <span className="dash-platform-fee-tile-sub">
              {PLATFORM_FEE_PERCENT.business === 0
                ? t('platformFee.tile.noFee')
                : t('platformFee.tile.fee', { amount: money(Math.round((base * PLATFORM_FEE_PERCENT.business) / 100)), fee: PLATFORM_FEE_PERCENT.business })}
            </span>
          </div>
        ) : null}
      </div>

      {paysForItself ? <p className="dash-platform-fee-pays">{t('platformFee.paysForItself', { plan: PLAN_LABEL[next] })}</p> : null}
    </section>
  )
}
